import React, { useEffect, useState } from "react";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import CssBaseline from "@mui/joy/CssBaseline";
import LoadingIcon from "../../../../../common/LoadingIcon";
import CustomizedInputsStyled from "./CustomizedInputStyled";
import ToastMessage from "../../../../../common/ToastMessage";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import useScreenWidth from "../../../../../store/useScreenWidth";
import useAddCustomers from "../../../../../store/ReactQueries/customers/useAddCustomers";
import useCustomersById from "../../../../../store/ReactQueries/customers/useCustomersById";
import useUpdateCustomers from "../../../../../store/ReactQueries/customers/useUpdateCustomer";
import { GenerateInput } from "../../../Assets/AddAsset";
import { EndPoints } from "../../../../../utils/apiEndpoints";
import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors } from "../../../../../domain/helpers/Errors";
import { validateSchema } from "../../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../../../store/useDarkModeStore";
import { darkModeBackgroudInputs, PAGESIZE } from "../../../../../utils/constants";
import { CustomerInterface } from "../../../../../models/TicketModule/Customer";
import { customerSchema } from "../../../../../domain/ValidationSchemas/Ticket/customer";
import "react-phone-number-input/style.css";

interface AddCustomerProps {
  mode: "Add" | "Edit" | "View";
}

export const AddCustomer: React.FC<AddCustomerProps> = ({ mode }) => {
  const isView = mode === "View";

  const navigate = useNavigate();
  const { id: customerId } = useParams<{ id: string }>();
  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();
  const isSmallScreen = useScreenWidth();
  const { data: customerById, error: customerError, isLoading } = useCustomersById(customerId || "");

  const page = 1;
  const pageSize = PAGESIZE;
  const initialState: CustomerInterface = {
    id: "",
    title: "",
    isDefault: false,
    code: "",
    contractTerms: "",
    companyId: "",
    phoneNo: "",
  };
  const addCustomer = useAddCustomers(page, pageSize);
  const updateCustomer = useUpdateCustomers(page, pageSize);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [desableSave, setDisableSave] = useState<boolean>(false);
  const [form, setForm] = useState<CustomerInterface>(initialState);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (customerError) {
      ToastMessage.Failed(`Error: ${customerError.message}`);
      navigate(-1);
    }
  }, [customerError]);

  useEffect(() => {
    if (customerId && customerById) {
      let result = customerById as CustomerInterface;
      setForm(result);
    }
  }, [customerId, customerById]);

  const generateInputs: GenerateInput<any>[] = [
    { id: 1, name: "title", label: "Title", type: "text" },
    { id: 2, name: "code", label: "Code", type: "text" },
    { id: 3, name: "companyId", label: "Company", type: "select" },
    { id: 4, name: "phoneNo", label: "PhoneNo", type: "phone" },
    { id: 5, name: "contractTerms", label: "ContractTerms", type: "multitext" },
    { id: 6, name: "isDefault", label: "isDefault", type: "checkbox" },
  ];

  const handleSave = async () => {
    const isValid = validateSchema(customerSchema, form);
    setErrors(isValid.errors);
    if (!isValid.result) return;

    try {
      await new Promise<void>((resolve, reject) => {
        const { id, title, code, contractTerms, isDefault, companyId, phoneNo } = form;
        let upForm: CustomerInterface = { id, title, code, contractTerms, isDefault, companyId, phoneNo };
        if (upForm.id === "") {
          const { id, ...formData } = upForm;
          addCustomer.mutate(formData, {
            onSuccess: () => handleSuccess(resolve),
            onError: (error) => ToastMessage.Failed(error.message),
          });
        } else {
          updateCustomer.mutate(upForm, {
            onSuccess: () => handleSuccess(resolve),
            onError: (error) => ToastMessage.Failed(error.message),
          });
        }
      });
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        console.log(ex.message);
      } else {
        console.log("An unknown error occurred");
      }
    }
  };

  const handleSuccess = (resolve: (value: void | PromiseLike<void>) => void) => {
    ToastMessage.Success();
    navigate(EndPoints.CUSTOMERS);
    resolve();
  };

  return (
    <div className="w-100 d-flex justify-content-center ">
      <CssBaseline />
      {isLoading ? (
        <LoadingIcon width={50} />
      ) : (
        <div className="d-flex flex-column align-items-center gap-2 w-100">
          <Sheet
            sx={{
              width: "90%",
              my: 1,
              py: 4,
              px: 4,
              display: "flex",
              flexDirection: "column",
              gap: 0,
              borderRadius: "md",
              boxShadow: "8px 8px 30px rgba(0, 0, 0, 0.2)",
              background: darkMode ? "var(--dark-mode-bg)" : "white",
            }}
          >
            <div
              className="mb-0 d-flex align-items-center"
              style={{
                color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)",
                fontSize: "20px",
              }}
            >
              {language["Customer"]} - {isView ? language["View"] : form.id === "" ? language["Add"] : language["Edit"]}
              {isView && (
                <IconButton
                  className={`ms-2 ${darkMode && "text-white"}`}
                  title={language["EditTicket"]}
                  onClick={() => navigate(`${EndPoints.CUSTOMERS}/edit/${form.id}`)}
                >
                  <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
                </IconButton>
              )}
            </div>
            <hr style={darkMode ? { color: "white", opacity: 1 } : {}} />
            <form
              className={`${isSmallScreen ? "d-flex flex-column" : "module-form-grid"} gap-2`}
              style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
            >
              {generateInputs?.map(({ id, name, label, type }) => (
                <CustomizedInputsStyled
                  key={id}
                  name={name}
                  label={label}
                  type={type}
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  view={isView}
                  loading={loading}
                />
              ))}
            </form>
            <hr className={darkMode ? "text-white opacity-100" : ""} />
            <div className="d-flex flex-row justify-content-end gap-2">
              <Button
                sx={{
                  fontSize: "16px",
                  background: darkMode ? darkModeBackgroudInputs : "white",
                  border: `1px solid var(--btn-color)`,
                  color: darkMode ? "white" : "var(--btn-color)",
                  "&:hover": {
                    background: darkMode ? darkModeBackgroudInputs : "white",
                    opacity: 0.7,
                  },
                }}
                onClick={() => navigate(EndPoints.CUSTOMERS)}
              >
                {language["Cancel"]}
              </Button>
              {!isView && (
                <Button
                  sx={{
                    fontSize: "16px",
                    background: "rgb(105, 108, 255)",
                    color: "white",
                    "&:hover": {
                      background: "rgb(85, 88, 235)",
                    },
                  }}
                  disabled={desableSave}
                  onClick={handleSave}
                >
                  {language["Save"]}
                </Button>
              )}
            </div>
          </Sheet>
        </div>
      )}
    </div>
  );
};
export default AddCustomer;
